import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { Link } from 'react-router';
import { telechargerBulletinPdf, telechargerComparaison, telechargerEtatPaie, telechargerLivrePaie, telechargerModeleComparaison, useActionsCalculPaie, useAgents, useBudgetLignes, useBulletinPaie, useCalculPaie, useCalculsPaie, useComparaisonPaie, useRubriquesPaie, useVariablesPaie } from '@/api/hooks';
import { LIBELLE_BLOC_RUBRIQUE, LIBELLE_ETAPE_CIRCUIT_PAIE, LIBELLE_STATUT_CALCUL, LIBELLE_TYPE_VARIABLE, type BlocRubriquePaie, type BulletinPaieResume, type DetailCalculPaie, type RubriquePaie, type TypeVariablePaie, type VariablePaie } from '@/api/types';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { StatutCalculBadge, StatutVariableBadge } from '@/components/calcul-paie';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, dateHeureFr, montant, nombre } from '@/lib/utils';

const ONGLETS = ['calculs', 'variables', 'rubriques'] as const;
type Onglet = (typeof ONGLETS)[number];
const LIBELLE_ONGLET: Record<Onglet, string> = { calculs: 'Calculs et bulletins', variables: 'Variables de paie', rubriques: 'Rubriques' };
const usd = (n: number | null | undefined) => montant(n, true);
const message = (e: unknown) => (e instanceof Error ? e.message : 'Opération impossible.');

/** Moteur de calcul de la paie (lot HP-4) : simulations et calcul réel d'une période, bulletins par agent, variables saisies et validées, référentiel des rubriques. */
export function CalculPaie() {
  const [params, setParams] = useSearchParams();
  const onglet: Onglet = ONGLETS.includes(params.get('onglet') as Onglet) ? (params.get('onglet') as Onglet) : 'calculs';
  const changer = (cle: string, valeur: string) => {
    const n = new URLSearchParams(params);
    if (valeur) n.set(cle, valeur);
    else n.delete(cle);
    setParams(n, { replace: true });
  };
  const liste = useCalculsPaie();
  const [lancement, setLancement] = useState<{ periodeId: string; simulation: boolean; tauxChangeCdfUsd: string; libelle: string; exclusions: Array<{ agentId: string; motif: string }> } | null>(null);
  if (liste.isLoading) return <Chargement />;
  if (liste.error || !liste.data) return <ErreurBloc erreur={liste.error} />;
  const calculId = params.get('calcul');
  const d = liste.data;
  const periodeOuverte = d.periodes.find((p) => p.statut === 'OUVERTE') ?? d.periodes[0];
  return (
    <div className="space-y-6">
      <PageHeader
        titre="Calcul de paie"
        sousTitre="Rémunération de référence → annuités → présences et variables → brut de période → assiettes → retenues légales → autres déductions → net restant à payer (spécifications HRMS et Paie, § 7). Montants en USD, impôt calculé en CDF au taux figé du calcul."
        actions={
          d.droits.calculer && periodeOuverte ? (
            <Button onClick={() => setLancement({ periodeId: periodeOuverte.id, simulation: true, tauxChangeCdfUsd: '', libelle: '', exclusions: [] })} data-testid="lancer-calcul">
              Simuler ou calculer
            </Button>
          ) : undefined
        }
      />
      <Tabs value={onglet} onValueChange={(v) => changer('onglet', v)}>
        <TabsList className="flex-wrap">
          {ONGLETS.filter((o) => o !== 'variables' || d.droits.voirBulletins).map((o) => (
            <TabsTrigger key={o} value={o}>
              {LIBELLE_ONGLET[o]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {onglet === 'calculs' && !calculId && <ListeCalculs liste={d} ouvrir={(id) => changer('calcul', id)} />}
      {onglet === 'calculs' && calculId && <DetailCalcul id={calculId} params={params} changer={changer} relancer={(c) => setLancement({ periodeId: c.periode.id, simulation: c.simulation, tauxChangeCdfUsd: c.tauxChangeCdfUsd ? String(c.tauxChangeCdfUsd) : '', libelle: c.libelle, exclusions: c.exclusions.map((e) => ({ agentId: e.agentId, motif: e.motif })) })} />}
      {onglet === 'variables' && <Variables periodes={d.periodes} mois={params.get('mois') ?? periodeOuverte?.mois ?? ''} changer={changer} />}
      {onglet === 'rubriques' && <Rubriques />}
      {lancement && (
        <LancementDialog
          initial={lancement}
          periodes={d.periodes}
          onClose={() => setLancement(null)}
          onFait={(c) => {
            setLancement(null);
            const n = new URLSearchParams(params);
            n.set('onglet', 'calculs');
            n.set('calcul', c.id);
            setParams(n, { replace: true });
          }}
        />
      )}
    </div>
  );
}

function ListeCalculs({ liste, ouvrir }: { liste: { calculs: DetailCalculPaie[] | import('@/api/types').ResumeCalculPaie[]; periodes: Array<{ id: string; mois: string; statut: string }> }; ouvrir: (id: string) => void }) {
  const calculs = liste.calculs;
  const dernier = calculs.find((c) => c.statut !== 'ANNULE');
  const figes = calculs.filter((c) => c.statut === 'FIGE').length;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Calculs" valeur={calculs.length} detail={`${figes} figé(s) · ${calculs.filter((c) => c.simulation).length} simulation(s)`} />
        <Kpi label="Dernier net à payer" valeur={dernier ? usd(dernier.totaux.netAPayer) : '—'} detail={dernier ? `${dernier.simulation ? 'simulation' : 'paie'} ${dernier.periode.mois} · ${dernier.totaux.bulletins} bulletin(s)` : 'aucun calcul'} />
        <Kpi label="Bulletins bloqués" valeur={dernier ? dernier.totaux.bloques : '—'} detail="à régulariser ou à exclure avant de figer" accent={dernier && dernier.totaux.bloques > 0 ? 'alerte' : dernier ? 'ok' : undefined} />
        <Kpi label="Périodes de présence" valeur={liste.periodes.length} detail={`${liste.periodes.filter((p) => p.statut === 'CLOTUREE').length} clôturée(s) : calcul réel possible`} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Calculs par période</CardTitle>
          <CardDescription>Une simulation se lance à tout moment ; le calcul réel attend la clôture de la période de présence, se recalcule tant qu'il est en brouillon et se fige par la paie avec son taux de change.</CardDescription>
        </CardHeader>
        <CardContent>
          {!calculs.length ? (
            <Vide message="Aucun calcul : lancez une simulation sur la période en cours." />
          ) : (
            <Table data-testid="liste-calculs">
              <TableHeader>
                <TableRow>
                  <TableHead>Période</TableHead>
                  <TableHead>Calcul</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="hidden md:table-cell text-right">Bulletins</TableHead>
                  <TableHead className="text-right">Net à payer</TableHead>
                  <TableHead className="hidden lg:table-cell text-right">Coût employeur</TableHead>
                  <TableHead className="hidden lg:table-cell">Calculé</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calculs.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.periode.mois}</TableCell>
                    <TableCell>
                      <button type="button" className="font-medium text-primary hover:underline" onClick={() => ouvrir(c.id)}>
                        n° {c.numero} · {c.libelle}
                      </button>
                      {c.tauxChangeCdfUsd ? <div className="text-xs text-muted-foreground">taux {nombre(c.tauxChangeCdfUsd, 2)} CDF/USD</div> : null}
                    </TableCell>
                    <TableCell>
                      <StatutCalculBadge statut={c.statut} simulation={c.simulation} />
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-right">
                      {c.totaux.bulletins}
                      {c.totaux.bloques > 0 && <div className="text-xs text-amber-700">{c.totaux.bloques} bloqué(s)</div>}
                      {c.totaux.exclus > 0 && <div className="text-xs text-muted-foreground">{c.totaux.exclus} exclu(s)</div>}
                    </TableCell>
                    <TableCell className="text-right">{usd(c.totaux.netAPayer)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-right">{usd(c.totaux.coutEmployeur)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                      {dateHeureFr(c.calculeLe)}
                      {c.calculePar ? <div>{c.calculePar}</div> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DetailCalcul({ id, params, changer, relancer }: { id: string; params: URLSearchParams; changer: (c: string, v: string) => void; relancer: (c: DetailCalculPaie) => void }) {
  const f = { structure: params.get('structure') ?? '', q: params.get('q') ?? '', anomalies: params.get('anomalies') ?? '' };
  const { data, isLoading, error } = useCalculPaie(id, { structure: f.structure || undefined, q: f.q || undefined, anomalies: f.anomalies || undefined });
  const actions = useActionsCalculPaie();
  const [motif, setMotif] = useState<{ action: 'figer' | 'annuler' | 'soumettre' | 'retirer' | 'valider' | 'retourner' | 'cloturer'; texte: string } | null>(null);
  const [fed, setFed] = useState(false);
  const agentId = params.get('agent');
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const c = data;
  const t = c.totaux;
  const MOTIF_OBLIGATOIRE = ['annuler', 'retirer', 'retourner'];
  const LIBELLES: Record<NonNullable<typeof motif>['action'], { titre: string; description: string; bouton: string; succes: string }> = {
    figer: { titre: 'Figer le calcul', description: 'Les montants et le taux de change sont arrêtés ; le calcul peut ensuite être soumis au circuit de validation. Aucun bulletin ne doit être bloqué.', bouton: 'Figer', succes: 'Calcul figé : taux et montants arrêtés.' },
    annuler: { titre: 'Annuler le calcul', description: 'Le calcul est conservé avec son motif d’annulation ; un nouveau calcul reste possible.', bouton: 'Annuler le calcul', succes: 'Calcul annulé.' },
    soumettre: { titre: 'Soumettre au contrôle RH', description: 'Le Responsable RH est prévenu ; puis validation du DRH, visas du DGA et du Contrôle de gestion, approbation du DF (D25).', bouton: 'Soumettre', succes: 'Paie soumise au contrôle RH.' },
    retirer: { titre: 'Retirer la soumission', description: 'La paie revient à l’état figé ; une nouvelle soumission reste possible.', bouton: 'Retirer', succes: 'Soumission retirée.' },
    valider: { titre: 'Valider, viser ou approuver', description: 'Votre décision fait passer la paie à l’étape suivante ; le commentaire est conservé dans le circuit.', bouton: 'Valider', succes: 'Décision enregistrée.' },
    retourner: { titre: 'Retourner la paie', description: 'La paie repasse en brouillon : la paie corrige, recalcule, fige et soumet à nouveau. Le motif est obligatoire et notifié.', bouton: 'Retourner', succes: 'Paie retournée en brouillon.' },
    cloturer: { titre: 'Clôturer la paie', description: 'Les retenues des prêts sociaux sont confirmées dans le module Prêts et les bulletins sont publiés aux agents (notification).', bouton: 'Clôturer', succes: 'Paie clôturée : bulletins publiés.' },
  };
  const decider = async () => {
    if (!motif) return;
    const texte = motif.texte.trim();
    try {
      if (motif.action === 'figer') await actions.figer.mutateAsync({ id: c.id, motif: texte || undefined });
      else if (motif.action === 'annuler') await actions.annuler.mutateAsync({ id: c.id, motif: texte });
      else if (motif.action === 'soumettre') await actions.soumettre.mutateAsync({ id: c.id, motif: texte || undefined });
      else if (motif.action === 'retirer') await actions.retirer.mutateAsync({ id: c.id, motif: texte });
      else if (motif.action === 'valider') await actions.decider.mutateAsync({ id: c.id, corps: { decision: 'VALIDER', motif: texte || undefined } });
      else if (motif.action === 'retourner') await actions.decider.mutateAsync({ id: c.id, corps: { decision: 'RETOURNER', motif: texte } });
      else await actions.cloturer.mutateAsync({ id: c.id, motif: texte || undefined });
      toast.success(LIBELLES[motif.action].succes);
      setMotif(null);
    } catch (e) {
      toast.error(message(e));
    }
  };
  const constater = async () => {
    try {
      await actions.constaterPaiement.mutateAsync({ id: c.id });
      toast.success('Paiement constaté depuis le FED payé.');
    } catch (e) {
      toast.error(message(e));
    }
  };
  const enAttente = actions.figer.isPending || actions.annuler.isPending || actions.soumettre.isPending || actions.retirer.isPending || actions.decider.isPending || actions.cloturer.isPending;
  return (
    <div className="space-y-4" data-testid="detail-calcul">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex flex-wrap items-center gap-2">
              {c.libelle} · {c.periode.mois} <StatutCalculBadge statut={c.statut} simulation={c.simulation} />
            </span>
            <span className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => telechargerLivrePaie(c.id, c.periode.mois, c.numero).catch((e) => toast.error(message(e)))}>
                Livre de paie (Excel)
              </Button>
              {c.droits.recalculer && (
                <Button size="sm" variant="outline" onClick={() => relancer(c)}>
                  Recalculer
                </Button>
              )}
              {c.droits.figer && (
                <Button size="sm" onClick={() => setMotif({ action: 'figer', texte: '' })} data-testid="figer-calcul">
                  Figer le calcul
                </Button>
              )}
              {c.droits.soumettre && (
                <Button size="sm" onClick={() => setMotif({ action: 'soumettre', texte: '' })} data-testid="soumettre-paie">
                  Soumettre au contrôle RH
                </Button>
              )}
              {c.droits.retirer && (
                <Button size="sm" variant="outline" onClick={() => setMotif({ action: 'retirer', texte: '' })}>
                  Retirer la soumission
                </Button>
              )}
              {c.droits.decider && (
                <Button size="sm" onClick={() => setMotif({ action: 'valider', texte: '' })} data-testid="valider-paie">
                  {c.droits.etape === 'VISA_DGA' || c.droits.etape === 'VISA_CG' ? 'Viser' : c.droits.etape === 'APPROBATION_DF' ? 'Approuver' : 'Valider'}
                </Button>
              )}
              {c.droits.retourner && (
                <Button size="sm" variant="outline" onClick={() => setMotif({ action: 'retourner', texte: '' })}>
                  Retourner
                </Button>
              )}
              {c.droits.etablirFed && (
                <Button size="sm" onClick={() => setFed(true)} data-testid="fed-paie">
                  Établir le FED de la paie
                </Button>
              )}
              {c.droits.constaterPaiement && (
                <Button size="sm" onClick={constater} disabled={actions.constaterPaiement.isPending}>
                  Constater le paiement
                </Button>
              )}
              {c.droits.cloturer && (
                <Button size="sm" onClick={() => setMotif({ action: 'cloturer', texte: '' })} data-testid="cloturer-paie">
                  Clôturer la paie
                </Button>
              )}
              {c.droits.annuler && (
                <Button size="sm" variant="ghost" onClick={() => setMotif({ action: 'annuler', texte: '' })}>
                  Annuler
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => changer('calcul', '')}>
                Fermer
              </Button>
            </span>
          </CardTitle>
          <CardDescription>
            Calcul n° {c.numero} du {dateHeureFr(c.calculeLe)}
            {c.calculePar ? ` par ${c.calculePar}` : ''} · période du {dateFr(c.periode.debut)} au {dateFr(c.periode.fin)} ({c.periode.statut === 'CLOTUREE' ? 'clôturée' : 'ouverte'}) · taux {c.tauxChangeCdfUsd ? `${nombre(c.tauxChangeCdfUsd, 2)} CDF/USD` : 'absent'}
            {c.figeLe ? ` · figé le ${dateHeureFr(c.figeLe)}${c.figePar ? ` par ${c.figePar}` : ''}` : ''}
            {c.annuleLe ? ` · annulé le ${dateHeureFr(c.annuleLe)}${c.commentaire ? ` : ${c.commentaire}` : ''}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Total des gains" valeur={usd(t.totalGains)} detail={`brut de période ${usd(t.brutPeriode)} · heures sup. ${usd(t.heuresSup)} · allocation ${usd(t.allocationExtralegale)}`} />
            <Kpi label="Total des retenues" valeur={usd(t.totalRetenues)} detail={`CNSS ${usd(t.cnss)} · impôt ${usd(t.impotUsd)} · prêts ${usd(t.retenuesPrets)} · autres ${usd(t.autresDeductions - t.retenuesPrets)}`} />
            <Kpi label="Net à payer" valeur={usd(t.netAPayer)} detail={`${t.bulletins} bulletin(s) · ${t.exclus} exclu(s)`} accent="ok" />
            <Kpi label="Coût employeur" valeur={usd(t.coutEmployeur)} detail={`charges patronales ${usd(t.patronales)}`} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Bulletins bloqués" valeur={t.bloques} detail={`${t.anomalies} anomalie(s) bloquante(s) · ${t.avertissements} avertissement(s)`} accent={t.bloques > 0 ? 'alerte' : 'ok'} />
            <Kpi label="Assiette sociale" valeur={usd(t.assietteSociale)} detail={`rémunération imposable ${usd(t.remunerationImposable)}`} />
            <Kpi label="Impôt (CDF)" valeur={`${nombre(t.impotCdf, 0)} CDF`} detail={`soit ${usd(t.impotUsd)} au taux du calcul`} />
            <Kpi label="Structures" valeur={t.parStructure.length} detail={t.parStructure.map((s) => `${s.code} ${nombre(s.netAPayer, 0)}`).slice(0, 4).join(' · ')} />
          </div>
          {!c.simulation && (
            <div className="rounded-md border p-3 text-sm" data-testid="circuit-paie">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium">Circuit de la paie (D25) · {LIBELLE_STATUT_CALCUL[c.statut]}</div>
                <div className="flex flex-wrap gap-2">
                  {c.fedId && (
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/fed/${c.fedId}`}>FED de la paie{c.referencePaiement ? ` · ${c.referencePaiement}` : ''}</Link>
                    </Button>
                  )}
                  {c.droits.etats && (
                    <>
                      {c.droits.virements && (
                        <Button size="sm" variant="outline" onClick={() => telechargerEtatPaie(c.id, 'virements', c.periode.mois).catch((e) => toast.error(message(e)))}>
                          Virements
                        </Button>
                      )}
                      {(['cnss', 'ipr', 'retenues', 'journal'] as const).map((t) => (
                        <Button key={t} size="sm" variant="outline" onClick={() => telechargerEtatPaie(c.id, t, c.periode.mois).catch((e) => toast.error(message(e)))}>
                          {t === 'cnss' ? 'État CNSS' : t === 'ipr' ? 'État IPR' : t === 'retenues' ? 'Retenues' : 'Journal comptable'}
                        </Button>
                      ))}
                    </>
                  )}
                </div>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Superviseur RH → Responsable RH → DRH → DGA et Contrôle de gestion → DF → trésorerie (FED, paiement) → clôture
                {c.payeLe ? ` · payée le ${dateFr(c.payeLe)}` : ''}
                {c.clotureLe ? ` · clôturée le ${dateHeureFr(c.clotureLe)}${c.cloturePar ? ` par ${c.cloturePar}` : ''}` : ''}
              </div>
              {c.circuit.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {[...c.circuit].reverse().map((d, i) => (
                    <li key={`${d.le}-${i}`} className={cn('flex flex-wrap items-baseline gap-x-2 text-xs', d.decision === 'RETOURNER' && 'text-amber-800')}>
                      <span className="w-36 shrink-0 text-muted-foreground">{dateHeureFr(d.le)}</span>
                      <span className="font-medium">{LIBELLE_ETAPE_CIRCUIT_PAIE[d.etape]}</span>
                      <span>· {d.decision === 'RETOURNER' ? (d.etape === 'SOUMISSION' ? 'soumission retirée' : 'retournée en brouillon') : d.decision === 'VISER' ? 'visée' : d.decision === 'APPROUVER' ? 'approuvée' : d.decision === 'SOUMETTRE' ? 'soumise' : d.decision === 'FED' ? 'FED établi' : d.decision === 'PAIEMENT' ? 'paiement constaté' : d.decision === 'CLOTURE' ? 'clôturée' : 'validée'} par {d.par}</span>
                      {d.commentaire ? <span className="text-muted-foreground">— {d.commentaire}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {c.exclusions.length > 0 && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="font-medium">Agents exclus du calcul ({c.exclusions.length})</div>
              <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                {c.exclusions.map((e) => (
                  <li key={e.agentId}>
                    {e.nom ?? e.agentId} · {e.motif}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
      {c.statut !== 'ANNULE' && !c.simulation && <Comparaison calculId={c.id} mois={c.periode.mois} />}
      {c.bulletins && (
        <Card>
          <CardHeader>
            <CardTitle>Bulletins de calcul</CardTitle>
            <CardDescription>Un bulletin par agent actif hors mandataires ; cliquez un agent pour lire ses lignes, ses assiettes et ses anomalies.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Input className="w-64" placeholder="Nom, matricule…" value={f.q} onChange={(e) => changer('q', e.target.value)} />
              <SelectNatif value={f.structure} onChange={(e) => changer('structure', e.target.value)}>
                <option value="">Toutes les structures</option>
                {t.parStructure.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} · {s.libelle}
                  </option>
                ))}
              </SelectNatif>
              <SelectNatif value={f.anomalies} onChange={(e) => changer('anomalies', e.target.value)}>
                <option value="">Tous les bulletins</option>
                <option value="true">Avec anomalie ou avertissement</option>
                <option value="bloques">Bloqués</option>
                <option value="false">Sans anomalie</option>
              </SelectNatif>
            </div>
            {agentId && <BulletinDetail calculId={c.id} agentId={agentId} mois={c.periode.mois} pdf={c.droits.pdf} onClose={() => changer('agent', '')} />}
            {!c.bulletins.length ? (
              <Vide message="Aucun bulletin ne correspond." />
            ) : (
              <div className="rounded-lg border bg-card">
                <Table data-testid="bulletins">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead className="hidden md:table-cell text-right">Jours payés</TableHead>
                      <TableHead className="hidden lg:table-cell text-right">Brut de période</TableHead>
                      <TableHead className="text-right">Gains</TableHead>
                      <TableHead className="hidden md:table-cell text-right">Retenues</TableHead>
                      <TableHead className="text-right">Net à payer</TableHead>
                      <TableHead className="hidden lg:table-cell">Variation</TableHead>
                      <TableHead>Anomalies</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {c.bulletins.map((b) => (
                      <LigneBulletinResume key={b.id} b={b} actif={agentId === b.agent.id} ouvrir={() => changer('agent', b.agent.id)} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {motif && (
        <Dialog open onOpenChange={(o) => !o && setMotif(null)}>
          <DialogContent data-testid="dialogue-motif-calcul">
            <DialogHeader>
              <DialogTitle>{LIBELLES[motif.action].titre}</DialogTitle>
              <DialogDescription>{LIBELLES[motif.action].description}</DialogDescription>
            </DialogHeader>
            <div className="space-y-1">
              <Label htmlFor="mc-motif">{MOTIF_OBLIGATOIRE.includes(motif.action) ? 'Motif *' : 'Commentaire'}</Label>
              <Textarea id="mc-motif" value={motif.texte} onChange={(e) => setMotif({ ...motif, texte: e.target.value })} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMotif(null)}>Fermer</Button>
              <Button onClick={decider} disabled={(MOTIF_OBLIGATOIRE.includes(motif.action) && motif.texte.trim().length < 3) || enAttente} data-testid="confirmer-motif-calcul">
                {LIBELLES[motif.action].bouton}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {fed && <FedDialog id={c.id} mois={c.periode.mois} onClose={() => setFed(false)} />}
    </div>
  );
}

function Comparaison({ calculId, mois }: { calculId: string; mois: string }) {
  const { data, isLoading, error } = useComparaisonPaie(calculId);
  const actions = useActionsCalculPaie();
  const [fichier, setFichier] = useState<File | null>(null);
  const [seuil, setSeuil] = useState('');
  const [commentaire, setCommentaire] = useState('');
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const k = data.comparaison;
  const importer = async () => {
    if (!fichier) return;
    try {
      const r = await actions.importerComparaison.mutateAsync({ id: calculId, fichier, seuil: seuil || undefined, commentaire: commentaire || undefined });
      toast.success(`Paie de référence rapprochée : ${r.comparaison?.totaux.rapproches ?? 0} agent(s), ${r.comparaison?.totaux.signales ?? 0} écart(s) signalé(s).`);
      setFichier(null);
    } catch (e) {
      toast.error(message(e));
    }
  };
  const ecart = (n: number | null) => (n === null ? <span className="text-muted-foreground">—</span> : <span className={cn(Math.abs(n) > (k?.seuil ?? 1) && 'font-medium text-destructive')}>{n >= 0 ? '+' : ''}{nombre(n, 2)}</span>);
  return (
    <Card data-testid="comparaison-paie">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Paie de comparaison (D30)
          <span className="flex flex-wrap gap-2">
            {data.droits.importer && (
              <Button size="sm" variant="outline" onClick={() => telechargerModeleComparaison().catch((e) => toast.error(message(e)))}>
                Modèle
              </Button>
            )}
            {k && data.droits.voirLignes && (
              <Button size="sm" variant="outline" onClick={() => telechargerComparaison(calculId, mois).catch((e) => toast.error(message(e)))}>
                Écarts (Excel)
              </Button>
            )}
          </span>
        </CardTitle>
        <CardDescription>Rapprochement d'une paie de référence du Fonds (classeur : Matricule, Nom, Brut, CNSS, Impôt, Net) avec les bulletins de ce calcul ; écart = calculé − référence ; tout écart au-delà du seuil est signalé.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.droits.importer && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="cp-fichier">Paie de référence (.xlsx)</Label>
              <Input id="cp-fichier" type="file" accept=".xlsx" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} data-testid="champ-comparaison" />
            </div>
            <div className="w-28 space-y-1">
              <Label htmlFor="cp-seuil">Seuil (USD)</Label>
              <Input id="cp-seuil" inputMode="decimal" value={seuil} onChange={(e) => setSeuil(e.target.value)} placeholder="paramètre" />
            </div>
            <div className="min-w-48 flex-1 space-y-1">
              <Label htmlFor="cp-commentaire">Commentaire</Label>
              <Input id="cp-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            <Button onClick={importer} disabled={!fichier || actions.importerComparaison.isPending} data-testid="rapprocher-comparaison">
              Rapprocher
            </Button>
          </div>
        )}
        {!k ? (
          <Vide message="Aucune paie de comparaison sur ce calcul." />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi label="Agents rapprochés" valeur={k.totaux.rapproches} detail={`${k.totaux.lignesFichier} ligne(s) du fichier ${k.fichier}`} />
              <Kpi label="Écarts signalés" valeur={k.totaux.signales} detail={`seuil ${nombre(k.seuil, 2)} USD · ${k.totaux.sansBulletin} sans bulletin · ${k.totaux.sansReference} sans référence`} accent={k.totaux.signales > 0 ? 'alerte' : 'ok'} />
              <Kpi label="Écart net total" valeur={`${k.totaux.ecartNet >= 0 ? '+' : ''}${usd(k.totaux.ecartNet)}`} detail={`écart absolu ${usd(k.totaux.ecartAbsoluNet)}`} accent={Math.abs(k.totaux.ecartNet) > k.seuil ? 'alerte' : 'ok'} />
              <Kpi label="Écarts brut · CNSS · impôt" valeur={`${nombre(k.totaux.ecartBrut, 2)} · ${nombre(k.totaux.ecartCnss, 2)} · ${nombre(k.totaux.ecartIpr, 2)}`} detail={`rapproché le ${dateHeureFr(k.le)}${k.auteur ? ` par ${k.auteur}` : ''}`} />
            </div>
            {k.commentaire && <div className="text-xs text-muted-foreground">{k.commentaire}</div>}
            {k.lignes && (
              <div className="rounded-lg border bg-card">
                <Table data-testid="lignes-comparaison">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="hidden lg:table-cell text-right">Écart brut</TableHead>
                      <TableHead className="hidden lg:table-cell text-right">Écart CNSS</TableHead>
                      <TableHead className="hidden md:table-cell text-right">Écart impôt</TableHead>
                      <TableHead className="text-right">Net référence</TableHead>
                      <TableHead className="text-right">Net calculé</TableHead>
                      <TableHead className="text-right">Écart net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {k.lignes.map((l) => (
                      <TableRow key={l.matricule} className={cn(l.signale && 'bg-amber-50/40')}>
                        <TableCell>
                          <div className="font-medium">{l.nom ?? '—'}</div>
                          <div className="text-xs text-muted-foreground">{l.matricule}</div>
                        </TableCell>
                        <TableCell>{l.statut === 'RAPPROCHE' ? <Badge variant="outline" className={cn(l.signale ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800')}>{l.signale ? 'écart' : 'conforme'}</Badge> : <Badge variant="destructive">{l.statut === 'SANS_BULLETIN' ? 'sans bulletin' : 'sans référence'}</Badge>}</TableCell>
                        <TableCell className="hidden lg:table-cell text-right">{ecart(l.ecarts.brut)}</TableCell>
                        <TableCell className="hidden lg:table-cell text-right">{ecart(l.ecarts.cnss)}</TableCell>
                        <TableCell className="hidden md:table-cell text-right">{ecart(l.ecarts.ipr)}</TableCell>
                        <TableCell className="text-right">{l.reference.net === null ? '—' : usd(l.reference.net)}</TableCell>
                        <TableCell className="text-right">{l.calcule.net === null ? '—' : usd(l.calcule.net)}</TableCell>
                        <TableCell className="text-right">{ecart(l.ecarts.net)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FedDialog({ id, mois, onClose }: { id: string; mois: string; onClose: () => void }) {
  const actions = useActionsCalculPaie();
  const lignes = useBudgetLignes('DFN');
  const [s, setS] = useState({ ligneBudgetaireId: '', modePaiement: 'Virement', semaineDecaissement: '' });
  const valider = async () => {
    try {
      await actions.etablirFed.mutateAsync({ id, corps: { ligneBudgetaireId: s.ligneBudgetaireId, modePaiement: s.modePaiement || undefined, semaineDecaissement: s.semaineDecaissement || undefined } });
      toast.success('FED de la paie établi : il suit le circuit des engagements puis la trésorerie.');
      onClose();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-fed-paie">
        <DialogHeader>
          <DialogTitle>Établir le FED de la paie · {mois}</DialogTitle>
          <DialogDescription>Une ligne par bénéficiaire : net du personnel (fichier des virements), CNSS (parts salariale et patronale), impôt professionnel, INPP, ONEM. Le FED suit le circuit habituel des engagements, puis la trésorerie le paie ; le paiement est constaté sur la paie.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1">
            <Label htmlFor="fp-ligne">Ligne budgétaire (Direction financière) *</Label>
            <SelectNatif id="fp-ligne" value={s.ligneBudgetaireId} onChange={(e) => setS({ ...s, ligneBudgetaireId: e.target.value })}>
              <option value="">Choisir une ligne</option>
              {(lignes.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code} · {l.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="fp-mode">Mode de paiement</Label>
              <Input id="fp-mode" value={s.modePaiement} onChange={(e) => setS({ ...s, modePaiement: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fp-semaine">Semaine de décaissement</Label>
              <Input id="fp-semaine" type="date" value={s.semaineDecaissement} onChange={(e) => setS({ ...s, semaineDecaissement: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={!s.ligneBudgetaireId || actions.etablirFed.isPending}>Établir le FED</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LigneBulletinResume({ b, actif, ouvrir }: { b: BulletinPaieResume; actif: boolean; ouvrir: () => void }) {
  return (
    <TableRow className={cn(actif && 'bg-muted/50', b.bloque && 'bg-red-50/40')}>
      <TableCell>
        <button type="button" className="font-medium text-primary hover:underline" onClick={ouvrir}>
          {b.agent.nom}
        </button>
        <div className="text-xs text-muted-foreground">
          {b.agent.matricule} · {b.agent.structure?.code ?? '—'}
          {b.agent.grade ? ` · ${b.agent.grade}` : ''}
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell text-right">
        {nombre(b.joursPayes, 2)}
        {b.joursDeduits > 0 && <div className="text-xs text-destructive">{nombre(b.joursDeduits, 1)} déduit(s)</div>}
      </TableCell>
      <TableCell className="hidden lg:table-cell text-right">{usd(b.brutPeriode)}</TableCell>
      <TableCell className="text-right">{usd(b.totalGains)}</TableCell>
      <TableCell className="hidden md:table-cell text-right">{usd(b.totalRetenues)}</TableCell>
      <TableCell className={cn('text-right font-medium', b.netAPayer < 0 && 'text-destructive')}>{usd(b.netAPayer)}</TableCell>
      <TableCell className="hidden lg:table-cell text-xs">{b.variation ? `${b.variation.ecart >= 0 ? '+' : ''}${nombre(b.variation.ecart, 2)} (${b.variation.pct ?? '—'} %) vs ${b.variation.mois}` : <span className="text-muted-foreground">première paie</span>}</TableCell>
      <TableCell>
        {b.bloque ? (
          <Badge variant="destructive">bloqué · {b.bloquantes}</Badge>
        ) : b.anomalies > 0 ? (
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
            {b.anomalies} avertissement(s)
          </Badge>
        ) : (
          <span className="text-xs text-emerald-700">aucune</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function BulletinDetail({ calculId, agentId, mois, pdf, onClose }: { calculId: string; agentId: string; mois: string; pdf: boolean; onClose: () => void }) {
  const { data, isLoading, error } = useBulletinPaie(calculId, agentId);
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const b = data;
  const fmt = (l: { montant: number; montantCdf: number | null; type: string; quantite: number | null }) => (l.type === 'INFO' && l.quantite !== null && l.montant === 0 ? `${nombre(l.quantite, 2)} j` : `${usd(l.montant)}${l.montantCdf !== null ? ` · ${nombre(l.montantCdf, 0)} CDF` : ''}`);
  return (
    <Card className="border-primary/30" data-testid="bulletin-detail">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          <span>
            {b.agent.nom} · {b.agent.matricule} · {b.agent.structure?.code ?? '—'}
            {b.agent.cadreDirection ? ' · cadre de direction' : ''}
          </span>
          <span className="flex flex-wrap gap-2">
            {pdf && (
              <Button size="sm" variant="outline" onClick={() => telechargerBulletinPdf(calculId, agentId, mois, b.agent.matricule).catch((e) => toast.error(message(e)))}>
                Bulletin (PDF)
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose}>
              Fermer
            </Button>
          </span>
        </CardTitle>
        <CardDescription>
          {nombre(b.joursPayes, 2)} jour(s) payé(s) · {nombre(b.joursDeduits, 1)} déduit(s) · {nombre(b.joursHorsContrat, 2)} hors contrat · {b.enfantsRetenus} enfant(s) retenu(s) · {b.personnesChargeFiscales} personne(s) à charge reconnue(s)
          {b.paiement ? ` · paiement par ${b.paiement.mode.toLowerCase()}${b.paiement.banque ? ` (${b.paiement.banque}${b.paiement.compte ? `, compte ${b.paiement.compte}` : ''})` : ''}` : ' · aucune coordonnée de paiement validée'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Total des gains" valeur={usd(b.totalGains)} detail={`brut de référence ${usd(b.brutReference)}`} />
          <Kpi label="Retenues légales" valeur={usd(b.cnss + b.impotUsd)} detail={`CNSS ${usd(b.cnss)} · impôt ${usd(b.impotUsd)} (${nombre(b.impotCdf, 0)} CDF)`} />
          <Kpi label="Autres déductions" valeur={usd(b.autresDeductions)} detail={`dont prêts ${usd(b.retenuesPrets)}`} />
          <Kpi label="Net à payer" valeur={usd(b.netAPayer)} detail={b.variation ? `${b.variation.ecart >= 0 ? '+' : ''}${nombre(b.variation.ecart, 2)} USD par rapport à ${b.variation.mois}` : 'première paie calculée'} accent={b.netAPayer < 0 ? 'critique' : 'ok'} />
        </div>
        {b.anomaliesDetail.length > 0 && (
          <ul className="space-y-1 text-sm">
            {b.anomaliesDetail.map((a) => (
              <li key={a.code + a.message} className={cn('rounded-md border px-3 py-1.5', a.bloquante ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800')}>
                <span className="font-medium">{a.bloquante ? 'Bloquante' : 'Avertissement'}</span> · {a.message}
              </li>
            ))}
          </ul>
        )}
        <div className="grid gap-4 lg:grid-cols-2">
          {b.blocs.map((bl) => (
            <div key={bl.bloc} className="rounded-lg border">
              <div className="border-b bg-muted/40 px-3 py-1.5 text-sm font-medium">{LIBELLE_BLOC_RUBRIQUE[bl.bloc as BlocRubriquePaie] ?? bl.libelle}</div>
              <Table>
                <TableBody>
                  {bl.lignes.map((l, i) => (
                    <TableRow key={`${l.code}-${i}`}>
                      <TableCell>
                        <div className={cn(l.type === 'INFO' && 'text-muted-foreground')}>{l.libelle}</div>
                        {(l.base !== null || l.taux !== null || l.detail) && (
                          <div className="text-xs text-muted-foreground">
                            {l.base !== null ? `base ${nombre(l.base, 2)}` : ''}
                            {l.taux !== null ? `${l.base !== null ? ' · ' : ''}taux ${nombre(l.taux, 2)} %` : ''}
                            {l.quantite !== null && l.type !== 'INFO' ? ` · ${nombre(l.quantite, 2)}` : ''}
                            {l.detail ? `${l.base !== null || l.taux !== null ? ' · ' : ''}${l.detail}` : ''}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className={cn('whitespace-nowrap text-right', l.type === 'RETENUE' && 'text-destructive', l.type === 'PATRONALE' && 'text-muted-foreground')}>
                        {l.type === 'RETENUE' ? '− ' : ''}
                        {fmt(l)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function LancementDialog({ initial, periodes, onClose, onFait }: { initial: { periodeId: string; simulation: boolean; tauxChangeCdfUsd: string; libelle: string; exclusions: Array<{ agentId: string; motif: string }> }; periodes: Array<{ id: string; mois: string; statut: string }>; onClose: () => void; onFait: (c: DetailCalculPaie) => void }) {
  const actions = useActionsCalculPaie();
  const [s, setS] = useState(initial);
  const periode = periodes.find((p) => p.id === s.periodeId);
  const lancer = async () => {
    try {
      const c = await actions.calculer.mutateAsync({ periodeId: s.periodeId, simulation: s.simulation, tauxChangeCdfUsd: s.tauxChangeCdfUsd ? Number(s.tauxChangeCdfUsd) : null, libelle: s.libelle || null, exclusions: s.exclusions });
      toast.success(`${c.simulation ? 'Simulation' : 'Calcul'} n° ${c.numero} : ${c.totaux.bulletins} bulletin(s), ${c.totaux.bloques} bloqué(s).`);
      onFait(c);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-calcul">
        <DialogHeader>
          <DialogTitle>Lancer un calcul de paie</DialogTitle>
          <DialogDescription>Une simulation se lance sur toute période ; le calcul réel attend la clôture de la période de présence et remplace le brouillon existant. Le taux de change vide reprend celui de la version du barème.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1">
            <Label htmlFor="lc-periode">Période de présence (mois de rémunération)</Label>
            <SelectNatif id="lc-periode" value={s.periodeId} onChange={(e) => setS({ ...s, periodeId: e.target.value })}>
              {periodes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.mois} · {p.statut === 'OUVERTE' ? 'ouverte' : 'clôturée'}
                </option>
              ))}
            </SelectNatif>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!s.simulation} disabled={periode?.statut !== 'CLOTUREE'} onChange={(e) => setS({ ...s, simulation: !e.target.checked })} /> Calcul réel (période clôturée){periode?.statut !== 'CLOTUREE' ? ' · indisponible : période ouverte' : ''}
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="lc-taux">Taux CDF pour 1 USD</Label>
              <Input id="lc-taux" inputMode="decimal" value={s.tauxChangeCdfUsd} onChange={(e) => setS({ ...s, tauxChangeCdfUsd: e.target.value })} placeholder="taux de la version du barème" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="lc-libelle">Libellé</Label>
              <Input id="lc-libelle" value={s.libelle} onChange={(e) => setS({ ...s, libelle: e.target.value })} />
            </div>
          </div>
          {s.exclusions.length > 0 && <div className="text-xs text-muted-foreground">{s.exclusions.length} agent(s) exclu(s) conservé(s) du calcul précédent.</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={lancer} disabled={!s.periodeId || actions.calculer.isPending} data-testid="confirmer-calcul">
            {s.simulation ? 'Simuler' : 'Calculer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Variables({ periodes, mois, changer }: { periodes: Array<{ id: string; mois: string; statut: string }>; mois: string; changer: (c: string, v: string) => void }) {
  const periode = periodes.find((p) => p.mois === mois) ?? periodes[0];
  const { data, isLoading, error } = useVariablesPaie(periode?.id);
  const actions = useActionsCalculPaie();
  const [saisie, setSaisie] = useState(false);
  const [decision, setDecision] = useState<{ v: VariablePaie; action: 'VALIDER' | 'REFUSER' | 'ANNULER'; motif: string } | null>(null);
  if (!periode) return <Vide message="Aucune période de présence : ouvrez-en une dans « Présences et variables »." />;
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const decider = async () => {
    if (!decision) return;
    try {
      if (decision.action === 'ANNULER') await actions.annulerVariable.mutateAsync({ id: decision.v.id, motif: decision.motif });
      else await actions.deciderVariable.mutateAsync({ id: decision.v.id, corps: { decision: decision.action, motif: decision.motif || undefined } });
      toast.success(decision.action === 'VALIDER' ? 'Variable validée : elle entre au prochain calcul.' : decision.action === 'REFUSER' ? 'Variable refusée.' : 'Variable annulée.');
      setDecision(null);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SelectNatif value={periode.mois} onChange={(e) => changer('mois', e.target.value)}>
          {periodes.map((p) => (
            <option key={p.id} value={p.mois}>
              {p.mois} · {p.statut === 'OUVERTE' ? 'ouverte' : 'clôturée'}
            </option>
          ))}
        </SelectNatif>
        {data.periode.figee && <Badge variant="outline">calcul figé : variables closes</Badge>}
        {data.droits.saisir && (
          <Button onClick={() => setSaisie(true)} data-testid="saisir-variable">
            Saisir une variable
          </Button>
        )}
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Variables de paie · {periode.mois}</CardTitle>
          <CardDescription>Avances, notes de débit, régularisations et primes exceptionnelles saisies par la paie ou les RH (jamais pour soi-même) et validées par le Directeur RH ou le Directeur financier ; seules les variables validées entrent au calcul.</CardDescription>
        </CardHeader>
        <CardContent>
          {!data.variables.length ? (
            <Vide message="Aucune variable sur cette période." />
          ) : (
            <Table data-testid="variables">
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Variable</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="hidden lg:table-cell">Saisie</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.variables.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>
                      <div className="font-medium">{v.agent ? `${v.agent.prenom} ${v.agent.nom}` : '—'}</div>
                      <div className="text-xs text-muted-foreground">
                        {v.agent?.matricule} · {v.agent?.structure?.code ?? '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>{v.libelle}</div>
                      <div className="text-xs text-muted-foreground">
                        {v.libelleType}
                        {v.moisOrigine ? ` · mois d'origine ${v.moisOrigine}` : ''} · {v.motif}
                      </div>
                    </TableCell>
                    <TableCell className={cn('text-right whitespace-nowrap', v.sens === 'RETENUE' && 'text-destructive')}>
                      {v.sens === 'RETENUE' ? '− ' : '+ '}
                      {usd(v.montant)}
                    </TableCell>
                    <TableCell>
                      <StatutVariableBadge statut={v.statut} />
                      {v.decidePar ? <div className="text-xs text-muted-foreground">{v.decidePar}{v.motifDecision ? ` : ${v.motifDecision}` : ''}</div> : null}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                      {v.saisiPar}
                      <div>{dateHeureFr(v.saisiLe)}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      {v.statut === 'SAISIE' && data.droits.valider && (
                        <span className="inline-flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => setDecision({ v, action: 'VALIDER', motif: '' })} data-testid={`valider-variable-${v.id}`}>
                            Valider
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDecision({ v, action: 'REFUSER', motif: '' })}>
                            Refuser
                          </Button>
                        </span>
                      )}
                      {(v.statut === 'SAISIE' || v.statut === 'VALIDEE') && data.droits.saisir && (
                        <Button size="sm" variant="ghost" onClick={() => setDecision({ v, action: 'ANNULER', motif: '' })}>
                          Annuler
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {saisie && <VariableDialog periodeId={periode.id} mois={periode.mois} onClose={() => setSaisie(false)} />}
      {decision && (
        <Dialog open onOpenChange={(o) => !o && setDecision(null)}>
          <DialogContent data-testid="dialogue-decision-variable">
            <DialogHeader>
              <DialogTitle>{decision.action === 'VALIDER' ? 'Valider la variable' : decision.action === 'REFUSER' ? 'Refuser la variable' : 'Annuler la variable'}</DialogTitle>
              <DialogDescription>
                {decision.v.libelleType} · {decision.v.agent ? `${decision.v.agent.prenom} ${decision.v.agent.nom}` : ''} · {usd(decision.v.montant)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1">
              <Label htmlFor="dv-motif">{decision.action === 'VALIDER' ? 'Commentaire' : 'Motif *'}</Label>
              <Textarea id="dv-motif" value={decision.motif} onChange={(e) => setDecision({ ...decision, motif: e.target.value })} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDecision(null)}>Fermer</Button>
              <Button onClick={decider} disabled={(decision.action !== 'VALIDER' && decision.motif.trim().length < 3) || actions.deciderVariable.isPending || actions.annulerVariable.isPending}>
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function VariableDialog({ periodeId, mois, onClose }: { periodeId: string; mois: string; onClose: () => void }) {
  const actions = useActionsCalculPaie();
  const agents = useAgents();
  const [s, setS] = useState<{ agentId: string; type: TypeVariablePaie; libelle: string; montant: string; moisOrigine: string; motif: string }>({ agentId: '', type: 'AVANCE', libelle: '', montant: '', moisOrigine: '', motif: '' });
  const regul = s.type === 'REGULARISATION_GAIN' || s.type === 'REGULARISATION_RETENUE';
  const valider = async () => {
    try {
      await actions.creerVariable.mutateAsync({ periodeId, corps: { agentId: s.agentId, type: s.type, libelle: s.libelle, montant: Number(s.montant), moisOrigine: regul ? s.moisOrigine : null, motif: s.motif } });
      toast.success('Variable saisie : le Directeur RH est prévenu pour validation.');
      onClose();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialogue-variable">
        <DialogHeader>
          <DialogTitle>Saisir une variable de paie · {mois}</DialogTitle>
          <DialogDescription>Montant en USD, positif ; le sens (gain ou retenue) vient du type. Une régularisation porte le mois d'origine corrigé.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1">
            <Label htmlFor="vd-agent">Agent *</Label>
            <SelectNatif id="vd-agent" value={s.agentId} onChange={(e) => setS({ ...s, agentId: e.target.value })}>
              <option value="">Choisir un agent</option>
              {(agents.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.prenom} {a.nom} · {a.matricule}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="vd-type">Type *</Label>
              <SelectNatif id="vd-type" value={s.type} onChange={(e) => setS({ ...s, type: e.target.value as TypeVariablePaie })}>
                {(Object.keys(LIBELLE_TYPE_VARIABLE) as TypeVariablePaie[]).map((t) => (
                  <option key={t} value={t}>
                    {LIBELLE_TYPE_VARIABLE[t]}
                  </option>
                ))}
              </SelectNatif>
            </div>
            <div className="space-y-1">
              <Label htmlFor="vd-montant">Montant (USD) *</Label>
              <Input id="vd-montant" inputMode="decimal" value={s.montant} onChange={(e) => setS({ ...s, montant: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="vd-libelle">Libellé *</Label>
            <Input id="vd-libelle" value={s.libelle} onChange={(e) => setS({ ...s, libelle: e.target.value })} />
          </div>
          {regul && (
            <div className="space-y-1">
              <Label htmlFor="vd-mois">Mois d'origine (AAAA-MM) *</Label>
              <Input id="vd-mois" value={s.moisOrigine} onChange={(e) => setS({ ...s, moisOrigine: e.target.value })} placeholder="2026-09" />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="vd-motif">Fondement (décision, référence, explication) *</Label>
            <Textarea id="vd-motif" value={s.motif} onChange={(e) => setS({ ...s, motif: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={valider} disabled={!s.agentId || !s.libelle.trim() || !(Number(s.montant) > 0) || s.motif.trim().length < 5 || (regul && !/^\d{4}-\d{2}$/.test(s.moisOrigine)) || actions.creerVariable.isPending}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Rubriques() {
  const { data, isLoading, error } = useRubriquesPaie();
  const actions = useActionsCalculPaie();
  const [edition, setEdition] = useState<RubriquePaie | null>(null);
  const [s, setS] = useState<{ libelle: string; imposable: boolean; cotisable: boolean; proratisable: boolean; participeAuNet: boolean; actif: boolean; compteComptable: string; description: string } | null>(null);
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const blocs = [...new Set(data.map((r) => r.bloc))];
  const ouvrir = (r: RubriquePaie) => {
    setEdition(r);
    setS({ libelle: r.libelle, imposable: r.imposable, cotisable: r.cotisable, proratisable: r.proratisable, participeAuNet: r.participeAuNet, actif: r.actif, compteComptable: r.compteComptable ?? '', description: r.description ?? '' });
  };
  const enregistrer = async () => {
    if (!edition || !s) return;
    try {
      const corps = edition.systeme ? { libelle: s.libelle, proratisable: s.proratisable, compteComptable: s.compteComptable || null, description: s.description || null } : { libelle: s.libelle, imposable: s.imposable, cotisable: s.cotisable, proratisable: s.proratisable, participeAuNet: s.participeAuNet, actif: s.actif, compteComptable: s.compteComptable || null, description: s.description || null };
      await actions.modifierRubrique.mutateAsync({ code: edition.code, corps });
      toast.success('Rubrique mise à jour (journalisée).');
      setEdition(null);
    } catch (e) {
      toast.error(message(e));
    }
  };
  const oui = (b: boolean) => (b ? <span className="text-emerald-700">oui</span> : <span className="text-muted-foreground">non</span>);
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Référentiel des rubriques</CardTitle>
          <CardDescription>Chaque rubrique porte son traitement fiscal, social, sa proratisation au trentième et sa participation au net (§ 7.1) ; les rubriques structurelles sont verrouillées. Toute modification est journalisée et s'applique aux calculs suivants.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {blocs.map((bloc) => (
            <div key={bloc} className="rounded-lg border">
              <div className="border-b bg-muted/40 px-3 py-1.5 text-sm font-medium">{LIBELLE_BLOC_RUBRIQUE[bloc]}</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rubrique</TableHead>
                    <TableHead className="hidden md:table-cell">Source</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="hidden lg:table-cell">Imposable</TableHead>
                    <TableHead className="hidden lg:table-cell">Cotisable</TableHead>
                    <TableHead className="hidden lg:table-cell">Trentième</TableHead>
                    <TableHead className="hidden md:table-cell">Net</TableHead>
                    <TableHead className="hidden md:table-cell">Compte</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data
                    .filter((r) => r.bloc === bloc)
                    .map((r) => (
                      <TableRow key={r.code} className={cn(!r.actif && 'text-muted-foreground line-through')}>
                        <TableCell>
                          <div className="font-medium">{r.libelle}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.code}
                            {r.systeme ? ' · structurelle' : ''}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{r.libelleSource}</TableCell>
                        <TableCell className="text-xs">{r.type === 'GAIN' ? 'Gain' : r.type === 'RETENUE' ? 'Retenue' : r.type === 'PATRONALE' ? 'Charge patronale' : 'Information'}</TableCell>
                        <TableCell className="hidden lg:table-cell text-xs">{oui(r.imposable)}</TableCell>
                        <TableCell className="hidden lg:table-cell text-xs">{oui(r.cotisable)}</TableCell>
                        <TableCell className="hidden lg:table-cell text-xs">{oui(r.proratisable)}</TableCell>
                        <TableCell className="hidden md:table-cell text-xs">{oui(r.participeAuNet)}</TableCell>
                        <TableCell className="hidden md:table-cell text-xs">{r.compteComptable ?? '—'}</TableCell>
                        <TableCell className="text-right">
                          {r.modifiable && (
                            <Button size="sm" variant="ghost" onClick={() => ouvrir(r)}>
                              Modifier
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </CardContent>
      </Card>
      {edition && s && (
        <Dialog open onOpenChange={(o) => !o && setEdition(null)}>
          <DialogContent data-testid="dialogue-rubrique">
            <DialogHeader>
              <DialogTitle>
                {edition.code} · {LIBELLE_BLOC_RUBRIQUE[edition.bloc]}
              </DialogTitle>
              <DialogDescription>{edition.systeme ? 'Rubrique structurelle : traitement fiscal et social verrouillés, toujours active.' : 'Le traitement s’applique aux calculs suivants ; les calculs figés ne changent pas.'}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3">
              <div className="space-y-1">
                <Label htmlFor="rb-libelle">Libellé</Label>
                <Input id="rb-libelle" value={s.libelle} onChange={(e) => setS({ ...s, libelle: e.target.value })} />
              </div>
              <div className="grid gap-2 sm:grid-cols-2 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={s.imposable} disabled={edition.systeme} onChange={(e) => setS({ ...s, imposable: e.target.checked })} /> Imposable</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={s.cotisable} disabled={edition.systeme} onChange={(e) => setS({ ...s, cotisable: e.target.checked })} /> Cotisable (CNSS)</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={s.proratisable} onChange={(e) => setS({ ...s, proratisable: e.target.checked })} /> Proratisée au trentième</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={s.participeAuNet} disabled={edition.systeme} onChange={(e) => setS({ ...s, participeAuNet: e.target.checked })} /> Participe au net</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={s.actif} disabled={edition.systeme} onChange={(e) => setS({ ...s, actif: e.target.checked })} /> Active</label>
              </div>
              <div className="space-y-1">
                <Label htmlFor="rb-compte">Compte comptable</Label>
                <Input id="rb-compte" value={s.compteComptable} onChange={(e) => setS({ ...s, compteComptable: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="rb-description">Description</Label>
                <Textarea id="rb-description" value={s.description} onChange={(e) => setS({ ...s, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEdition(null)}>Annuler</Button>
              <Button onClick={enregistrer} disabled={s.libelle.trim().length < 2 || actions.modifierRubrique.isPending}>Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
